import React, { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useEticketingDashboardStatsQuery } from "../../../api/e-ticketing/e-ticketing.hooks";

const EticketDashboard = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const serviceId = searchParams.get("service_id");

  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  const itemsPerPage = 10;

  // Build query params
  const queryParams = useMemo(() => {
    const params = {};
    if (serviceId) params.service_id = serviceId;
    if (dateRange.start_date) params.start_date = dateRange.start_date;
    if (dateRange.end_date) params.end_date = dateRange.end_date;
    return params;
  }, [serviceId, dateRange]);

  const { data, isLoading, error } = useEticketingDashboardStatsQuery(queryParams);

  const topTabs = data?.topTabs || {
    totalReservations: 0,
    totalRefund: 0,
    freeTierTickets: 0,
    netRevenue: 0,
  };

  const tableData = data?.tableData || [];

  // Group data by route_name
  const groupedByRoute = useMemo(() => {
    const groups = {};
    tableData.forEach((item) => {
      const routeName = item.route_name || "Unknown Route";
      if (!groups[routeName]) {
        groups[routeName] = [];
      }
      groups[routeName].push(item);
    });
    return groups;
  }, [tableData]);

  // Filter grouped data based on search term
  const filteredRoutes = useMemo(() => {
    if (!searchTerm) return groupedByRoute;

    const filtered = {};
    const lowerSearch = searchTerm.toLowerCase();

    Object.keys(groupedByRoute).forEach((routeName) => {
      const filteredItems = groupedByRoute[routeName].filter((item) =>
        item.description.toLowerCase().includes(lowerSearch) ||
        item.route_name.toLowerCase().includes(lowerSearch) ||
        item.price.toString().includes(lowerSearch)
      );

      if (filteredItems.length > 0 || routeName.toLowerCase().includes(lowerSearch)) {
        filtered[routeName] = filteredItems;
      }
    });

    return filtered;
  }, [groupedByRoute, searchTerm]);

  const handleDateRangeChange = (field, value) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
    setCurrentPage(0);
  };

  const clearDateRange = () => {
    setDateRange({
      start_date: "",
      end_date: "",
    });
    setCurrentPage(0);
  };

  // Calculate totals for each route
  const calculateRouteTotals = (items) => {
    return items.reduce(
      (acc, item) => ({
        quantity: acc.quantity + item.quantity,
        total: acc.total + item.total,
      }),
      { quantity: 0, total: 0 }
    );
  };

  return (
    <div className="col-12">
      {/* Top Stats Tabs */}
      <div className="row mb-4">
        <div className="col-xl-3 col-lg-6 col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h4 className="mb-1">{topTabs.totalReservations}</h4>
                  <p className="mb-0 text-muted">Total Reservations</p>
                </div>
                <div className="icon-box bg-primary-light">
                  <i className="fa fa-ticket-alt text-primary"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-lg-6 col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h4 className="mb-1">{topTabs.totalRefund}</h4>
                  <p className="mb-0 text-muted">Total Refund</p>
                </div>
                <div className="icon-box bg-danger-light">
                  <i className="fa fa-undo text-danger"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-lg-6 col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h4 className="mb-1">{topTabs.freeTierTickets}</h4>
                  <p className="mb-0 text-muted">Free Tier Tickets</p>
                </div>
                <div className="icon-box bg-success-light">
                  <i className="fa fa-gift text-success"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-lg-6 col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h4 className="mb-1">Rs. {topTabs.netRevenue.toLocaleString()}</h4>
                  <p className="mb-0 text-muted">Net Revenue</p>
                </div>
                <div className="icon-box bg-warning-light">
                  <i className="fa fa-dollar-sign text-warning"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        {/* Header */}
        <div className="card-header">
          <h4 className="card-title m-0">E-Ticketing Dashboard</h4>
        </div>

        {/* Date Range Filter */}
        <div className="card-body border-bottom">
          <div className="row align-items-end">
            <div className="col-md-4">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={dateRange.start_date}
                onChange={(e) => handleDateRangeChange("start_date", e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={dateRange.end_date}
                min={dateRange.start_date}
                onChange={(e) => handleDateRangeChange("end_date", e.target.value)}
                disabled={!dateRange.start_date}
              />
            </div>
            <div className="col-md-4">
              <button
                className="btn btn-outline-secondary"
                onClick={clearDateRange}
                disabled={!dateRange.start_date && !dateRange.end_date}
              >
                Clear Filter
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="card-body border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <input
              type="text"
              className="form-control w-auto"
              placeholder="Search by route name, description or price"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
            />
          </div>
        </div>

        {/* Tables grouped by route */}
        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">
              Failed to load dashboard data. Please try again.
            </div>
          ) : Object.keys(filteredRoutes).length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No data found.</p>
            </div>
          ) : (
            <>
              {Object.entries(filteredRoutes).map(([routeName, items], routeIndex) => {
                const routeTotals = calculateRouteTotals(items);
                return (
                  <div key={routeIndex} className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">{routeName}</h5>
                      <div className="text-muted">
                        <small>
                          Total Quantity: <strong>{routeTotals.quantity}</strong> | 
                          Total Revenue: <strong>Rs. {routeTotals.total.toLocaleString()}</strong>
                        </small>
                      </div>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Description</th>
                            <th>Price (Rs.)</th>
                            <th>Quantity</th>
                            <th>Total (Rs.)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, i) => (
                            <tr key={item.id}>
                              <td>{i + 1}</td>
                              <td>{item.description}</td>
                              <td>{item.price.toLocaleString()}</td>
                              <td>{item.quantity}</td>
                              <td>{item.total.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="table-secondary">
                          <tr>
                            <td colSpan="3" className="text-end"><strong>Subtotal:</strong></td>
                            <td><strong>{routeTotals.quantity}</strong></td>
                            <td><strong>Rs. {routeTotals.total.toLocaleString()}</strong></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EticketDashboard;
